import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, Button, Row, Col, Typography, Space, Divider } from 'antd';
import { UserOutlined, MailOutlined, PhoneOutlined, HomeOutlined, SaveOutlined, CloseOutlined, IdcardOutlined, TeamOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import axiosInstance from '../../variables/api/axiosInstance';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const EditIntermediaire = ({ show, handleClose, intermediaire, agence }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [mailExists, setMailExists] = useState(false);
    const navigate = useNavigate();

    // Thème de couleurs
    const colors = {
        primary: '#242166',
        secondary: '#1890ff',
        success: '#52c41a',
        error: '#ff4d4f',
        gradient: 'linear-gradient(135deg, #242166 0%, #1890ff 100%)',
        lightBg: '#f5f7fa'
    };

    const checkIfEmailExists = async (email) => {
        try {
            const res = await axiosInstance.get(`api/utilisateur/check/${email}`);
            if (res.data && email !== intermediaire?.personne?.email) {
                toast.warning("Il existe déjà un utilisateur avec cette adresse mail dans le système!");
            }
            setMailExists(res.data && email !== intermediaire?.personne?.email);
            return res.data && email !== intermediaire?.personne?.email;
        } catch (err) {
            console.log(err);
            return false;
        }
    };

    const handleEmailChange = (e) => {
        const email = e.target.value;
        if (email) {
            const fullEmail = agence?.organisme?.domaine ? `${email}@${agence?.organisme?.domaine}` : email;
            checkIfEmailExists(fullEmail);
        }
    };

    const handleSubmit = async (values) => {
        setLoading(true);

        try {
            let email = values.email;
            if (agence?.organisme?.domaine) {
                email += "@" + agence?.organisme?.domaine;
            }

            const emailExists = await checkIfEmailExists(email);

            if (emailExists) {
                setLoading(false);
                return;
            }

            const updatedIntermediaire = {
                ...intermediaire,
                personne: {
                    ...intermediaire.personne,
                    nom: values.nom,
                    prenom: values.prenom,
                    email: email,
                    contact: [values.telephone],
                    genre: values.genre,
                    adresse: values.adresseResidence,
                },
                type: values.type,
                agence: agence,
                codeIntermediaire: values.codeIntermediaire
            };

            const res = await axiosInstance.put(`/api/intermediaire`, JSON.stringify(updatedIntermediaire));
            console.log(res.data);
            toast.success("Intermédiaire mis à jour avec succès");
            navigate(`/intermediaire/${res.data.codeId}`, { replace: true });
            handleClose();
        } catch (err) {
            console.log(err);
            toast.error("Erreur lors de la mise à jour de l'intermédiaire");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (intermediaire) {
            form.setFieldsValue({
                nom: intermediaire?.personne?.nom,
                prenom: intermediaire?.personne?.prenom,
                genre: intermediaire?.personne?.genre,
                adresseResidence: intermediaire?.personne?.adresse,
                telephone: intermediaire?.personne?.contact && intermediaire?.personne?.contact[0],
                email: agence?.organisme?.domaine
                    ? intermediaire?.personne?.email?.split("@")[0]
                    : intermediaire?.personne?.email,
                type: intermediaire?.type,
                codeIntermediaire: intermediaire?.codeIntermediaire
            });
        }
    }, [intermediaire, agence, form]);

    const phoneValidator = (_, value) => {
        const phonePattern = /^(22|90|91|92|93|96|98|99|70|71|79)\d{6}$/;
        if (!value) {
            return Promise.reject(new Error('Numéro de téléphone requis'));
        }
        if (!phonePattern.test(value)) {
            return Promise.reject(new Error('Numéro de téléphone invalide'));
        }
        return Promise.resolve();
    };

    return (
        <Modal
            open={show}
            onCancel={handleClose}
            footer={null}
            width={900}
            centered
            style={{ top: 20 }}
            closeIcon={<CloseOutlined style={{ color: colors.primary, fontSize: '18px' }} />}
        >
            <div style={{ padding: '8px 0' }}>
                {/* Header */}
                <div style={{
                    textAlign: 'center',
                    marginBottom: '32px',
                    paddingBottom: '24px',
                    borderBottom: `3px solid ${colors.primary}`
                }}>
                    <div style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        background: colors.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 16px',
                        boxShadow: '0 4px 12px rgba(36, 33, 102, 0.3)'
                    }}>
                        <UserOutlined style={{ fontSize: '32px', color: 'white' }} />
                    </div>
                    <Title level={3} style={{
                        margin: '0 0 8px 0',
                        color: colors.primary
                    }}>
                        Modifier l'intermédiaire
                    </Title>
                    <Text style={{ color: '#8c8c8c', fontSize: '14px' }}>
                        Modifier les informations de l'intermédiaire de l'agence
                    </Text>
                </div>

                {/* Form */}
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    requiredMark="optional"
                >
                    <Row gutter={16}>
                        {/* Nom */}
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="nom"
                                label={<Text strong style={{ color: colors.primary }}>Nom</Text>}
                                rules={[
                                    { required: true, message: 'Nom requis' },
                                    { min: 3, message: 'Le nom doit avoir au moins 3 caractères' }
                                ]}
                            >
                                <Input
                                    prefix={<UserOutlined style={{ color: colors.secondary }} />}
                                    placeholder="Entrez le nom"
                                    size="large"
                                    style={{ borderRadius: '8px' }}
                                />
                            </Form.Item>
                        </Col>

                        {/* Prénom */}
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="prenom"
                                label={<Text strong style={{ color: colors.primary }}>Prénom(s)</Text>}
                                rules={[
                                    { required: true, message: 'Prénom requis' },
                                    { min: 3, message: 'Le prénom doit avoir au moins 3 caractères' }
                                ]}
                            >
                                <Input
                                    prefix={<UserOutlined style={{ color: colors.secondary }} />}
                                    placeholder="Entrez le prénom"
                                    size="large"
                                    style={{ borderRadius: '8px' }}
                                />
                            </Form.Item>
                        </Col>

                        {/* Code Intermédiaire */}
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="codeIntermediaire"
                                label={<Text strong style={{ color: colors.primary }}>Code de l'intermédiaire</Text>}
                                rules={[{ required: true, message: 'Code intermédiaire requis' }]}
                            >
                                <Input
                                    prefix={<IdcardOutlined style={{ color: colors.secondary }} />}
                                    placeholder="Entrez le code intermédiaire"
                                    size="large"
                                    style={{ borderRadius: '8px' }}
                                />
                            </Form.Item>
                        </Col>

                        {/* Genre */}
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="genre"
                                label={<Text strong style={{ color: colors.primary }}>Genre</Text>}
                                rules={[{ required: true, message: 'Genre requis' }]}
                            >
                                <Select
                                    placeholder="Sélectionnez le genre"
                                    size="large"
                                    style={{ borderRadius: '8px' }}
                                >
                                    <Option value="MASCULIN">Masculin</Option>
                                    <Option value="FEMININ">Féminin</Option>
                                </Select>
                            </Form.Item>
                        </Col>

                        {/* Type d'intermédiaire */}
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="type"
                                label={<Text strong style={{ color: colors.primary }}>Type d'intermédiaire</Text>}
                                rules={[{ required: true, message: 'Veuillez sélectionner le type d\'intermédiaire' }]}
                            >
                                <Select
                                    placeholder="Sélectionnez le type d'intermédiaire"
                                    size="large"
                                    suffixIcon={<TeamOutlined style={{ color: colors.secondary }} />}
                                    style={{ borderRadius: '8px' }}
                                >
                                    <Option value="AGENT_GENERAL">Agent général</Option>
                                    <Option value="APPORTEUR_REGULIER">Apporteur régulier</Option>
                                    <Option value="APPORTEUR_OCCASIONNEL">Apporteur occasionnel</Option>
                                    <Option value="COURTIER">Courtier</Option>
                                </Select>
                            </Form.Item>
                        </Col>

                        {/* Email */}
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="email"
                                label={<Text strong style={{ color: colors.primary }}>Adresse mail</Text>}
                                rules={[
                                    { required: true, message: 'Adresse mail requis' },
                                    { type: 'email', message: 'L\'adresse email n\'est pas valide' }
                                ]}
                            >
                                <Input
                                    prefix={<MailOutlined style={{ color: colors.secondary }} />}
                                    suffix={agence?.organisme?.domaine && (
                                        <Text style={{ color: '#8c8c8c' }}>@{agence?.organisme?.domaine}</Text>
                                    )}
                                    placeholder="Veuillez renseigner l'adresse mail"
                                    size="large"
                                    onChange={handleEmailChange}
                                    style={{ borderRadius: '8px' }}
                                />
                            </Form.Item>
                        </Col>

                        {/* Téléphone */}
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="telephone"
                                label={<Text strong style={{ color: colors.primary }}>Numéro de téléphone</Text>}
                                rules={[{ validator: phoneValidator }]}
                            >
                                <Input
                                    prefix={<PhoneOutlined style={{ color: colors.secondary }} />}
                                    addonBefore={<Text strong>TG (+228)</Text>}
                                    placeholder="00 00 00 00"
                                    size="large"
                                    maxLength={8}
                                    style={{ borderRadius: '8px' }}
                                />
                            </Form.Item>
                        </Col>

                        {/* Adresse */}
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="adresseResidence"
                                label={<Text strong style={{ color: colors.primary }}>Adresse de résidence</Text>}
                            >
                                <TextArea
                                    placeholder="L'adresse de résidence de l'intermédiaire"
                                    maxLength={255}
                                    rows={4}
                                    showCount
                                    style={{ borderRadius: '8px' }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider style={{ margin: '24px 0', borderColor: '#f0f0f0' }} />

                    {/* Footer Buttons */}
                    <Form.Item style={{ marginBottom: 0, textAlign: 'center' }}>
                        <Space size="middle">
                            <Button
                                size="large"
                                onClick={handleClose}
                                icon={<CloseOutlined />}
                                style={{
                                    borderRadius: '8px',
                                    minWidth: '120px',
                                    height: '44px'
                                }}
                            >
                                Annuler
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                size="large"
                                loading={loading}
                                icon={<SaveOutlined />}
                                style={{
                                    background: colors.gradient,
                                    border: 'none',
                                    borderRadius: '8px',
                                    minWidth: '140px',
                                    height: '44px',
                                    boxShadow: '0 4px 12px rgba(36, 33, 102, 0.3)'
                                }}
                            >
                                {loading ? 'Enregistrement...' : 'Enregistrer'}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </div>
        </Modal>
    );
};

export default EditIntermediaire;